import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Target, TrendingUp, TrendingDown, Loader2, RefreshCw, Filter,
  Sparkles, ArrowRight, GraduationCap,
} from "lucide-react";

const F = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

function SetupCard({ s }) {
  const isBull = s.kind === "bull";
  const distFromEntry = s.last_close && s.plan?.entry
    ? ((s.last_close - s.plan.entry) / s.plan.entry * 100)
    : null;
  const dispSym = s.asset_type === "crypto"
    ? s.symbol.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    : s.symbol;
  return (
    <Link to={`/asset/${s.asset_type}/${s.symbol}`}
          data-testid={`setup-${s.symbol}`}
          className="block group">
      <Card className={`p-4 rounded-2xl border-2 transition-all group-hover:scale-[1.01] group-hover:shadow-lg
        ${isBull ? "border-[hsl(var(--gain))]/40 bg-[hsl(var(--gain))]/[0.03]"
                 : "border-[hsl(var(--loss))]/40 bg-[hsl(var(--loss))]/[0.03]"}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-display font-bold text-lg">{dispSym}</span>
              <Badge variant="outline" className="text-[10px] uppercase">{s.asset_type}</Badge>
            </div>
            <div className="flex items-center gap-1.5">
              {isBull ? <TrendingUp className="w-3.5 h-3.5 text-[hsl(var(--gain))]" />
                      : <TrendingDown className="w-3.5 h-3.5 text-[hsl(var(--loss))]" />}
              <span className={`text-xs font-semibold ${isBull ? "gain" : "loss"}`}>{s.name}</span>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-mono-tab font-bold ${
              s.confidence >= 70 ? "gain" : s.confidence >= 50 ? "text-[hsl(var(--ai))]" : "loss"
            }`}>{s.confidence}%</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">confidence</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="p-1.5 rounded bg-[hsl(var(--gain))]/10">
            <div className="text-[10px] uppercase text-muted-foreground">Entry</div>
            <div className="font-mono-tab font-bold gain">${F(s.plan?.entry)}</div>
          </div>
          <div className="p-1.5 rounded bg-[hsl(var(--loss))]/10">
            <div className="text-[10px] uppercase text-muted-foreground">Stop</div>
            <div className="font-mono-tab font-bold loss">${F(s.plan?.stop)}</div>
          </div>
          <div className="p-1.5 rounded bg-[hsl(var(--ai))]/10">
            <div className="text-[10px] uppercase text-muted-foreground">Target</div>
            <div className="font-mono-tab font-bold text-[hsl(var(--ai))]">${F(s.plan?.target)}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="text-muted-foreground">
            Price now <span className="font-mono-tab text-foreground">${F(s.last_close)}</span>
            {distFromEntry != null && (
              <span className={`ml-2 font-mono-tab ${Math.abs(distFromEntry) < 2 ? "gain" : "text-muted-foreground"}`}>
                ({distFromEntry >= 0 ? "+" : ""}{distFromEntry.toFixed(1)}%)
              </span>
            )}
          </span>
          <span className="text-[hsl(var(--ai))] group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
            Trade this <ArrowRight className="w-3 h-3" />
          </span>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground">R:R <span className="text-foreground">1:{F(s.plan?.rr, 2)}</span></div>
      </Card>
    </Link>
  );
}

export default function Setups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | bull | bear | stock | crypto

  const load = (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    api.get("/copilot/setups-today")
      .then((r) => setData(r.data))
      .catch(() => setData({ setups: [], count: 0 }))
      .finally(() => { setLoading(false); setRefreshing(false); });
  };
  useEffect(() => { load(); }, []);

  const setups = (data?.setups || []).filter((s) => {
    if (filter === "all") return true;
    if (filter === "bull" || filter === "bear") return s.kind === filter;
    return s.asset_type === filter;
  });

  const chip = (v, label) => (
    <button onClick={() => setFilter(v)}
            data-testid={`setups-filter-${v}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
              filter === v ? "bg-foreground text-background border-foreground"
                           : "border-border text-muted-foreground hover:border-foreground/40"
            }`}>{label}</button>
  );

  return (
    <div className="space-y-6" data-testid="setups-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Chart Copilot</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <Target className="w-7 h-7" /> Setups today
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every stock &amp; crypto we scanned that's currently forming a high-confidence chart setup.
            Each card shows entry, stop, target and risk/reward — tap to open the chart with the Copilot.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => load(true)} disabled={refreshing}
                  data-testid="setups-refresh"
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Beginner banner */}
      <Card className="p-4 rounded-2xl bg-[hsl(var(--ai))]/5 border border-[hsl(var(--ai))]/20" data-testid="setups-beginner-banner">
        <div className="flex items-start gap-3">
          <GraduationCap className="w-5 h-5 text-[hsl(var(--ai))] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-foreground mb-1">New to trading? Read this first.</div>
            <p className="text-muted-foreground leading-relaxed">
              These are patterns other traders look for — <span className="text-foreground">not guarantees</span>.
              Even the best setup fails 30-40% of the time. The magic is the <span className="text-foreground">risk/reward</span>:
              a "1:3" R:R means your winners can be 3× bigger than your losers, so you're profitable even at a 40% win rate.
              Practice these on paper before risking real money.
            </p>
          </div>
        </div>
      </Card>

      {/* Filter chips */}
      <div className="flex items-center flex-wrap gap-2">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        {chip("all", "All")}
        {chip("bull", "Bullish")}
        {chip("bear", "Bearish")}
        {chip("stock", "Stocks")}
        {chip("crypto", "Crypto")}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="setups-loading">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning the market for setups…
        </div>
      ) : setups.length === 0 ? (
        <Card className="p-8 rounded-2xl text-center" data-testid="setups-empty">
          <Sparkles className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="font-display text-lg">No high-confidence setups match right now</div>
          <div className="text-sm text-muted-foreground mt-1">
            {filter === "all"
              ? "The market's choppy — great traders skip more setups than they take."
              : "Try switching filters — there might be setups in another category."}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="setups-grid">
          {setups.map((s, i) => <SetupCard key={`${s.asset_type}-${s.symbol}-${i}`} s={s} />)}
        </div>
      )}

      {data?.scanned_at && (
        <div className="text-[10px] text-center text-muted-foreground">
          Scanned {new Date(data.scanned_at).toLocaleString()} · showing {setups.length} of {data.count} setups
        </div>
      )}
    </div>
  );
}
